/**
 * phase-compiler-screen-batch item 2 (Sharon, 2026-07-23) — ONE definition of
 * classifier confidence and its bands, for every surface that shows it.
 *
 * ## The defect this closes, and what it actually was
 *
 * For `1-0-day-120-responses.pdf` the CTD tree showed an amber "low conf." badge
 * while the Inspector showed "HIGH CONFIDENCE — 92%". The suspected cause was a
 * percentage-vs-decimal scale mix-up. **It was not.** Both surfaces already
 * normalised 0–1 → 0–100 correctly. They were reading **two different columns**:
 *
 *   Documents row (verified 2026-07-23, Eupravia Seq 0001):
 *     FileName                     ConfidenceScore   ClassificationConfidence
 *     1-0-day-120-responses.pdf    0                 0.92
 *     1-2-cover-letter.pdf         74                0.74
 *
 * - The tree (`docReviewFlags`) read **`ConfidenceScore`** → `0` → below the
 *   40 threshold → "low conf."
 * - The Inspector read **`ClassificationConfidence`** → `0.92` → 92% → "HIGH".
 *
 * Both were faithfully reporting the field they were given. `ConfidenceScore` is
 * the legacy column: the current classifier writes `ClassificationConfidence`
 * and leaves `ConfidenceScore` at 0, so every AI-classified row reads as
 * maximally-unconfident to any consumer still on the old column. The cover
 * letter agrees across both columns (74 / 0.74) only because it predates that
 * split, which is exactly why the bug looked intermittent.
 *
 * ## The rule
 *
 * `ClassificationConfidence` is authoritative. `ConfidenceScore` is a fallback
 * for rows written before the split. A `ConfidenceScore` of 0 alongside a
 * present `ClassificationConfidence` means "not populated", NOT "zero
 * confidence" — treating that 0 as a real score is the bug itself.
 *
 * Scale is normalised on read: a value ≤ 1 is a 0–1 float, anything above is
 * already 0–100 (ICH-CONFIDENCE-01 documents both storage shapes).
 */

/** Bands, in the product's existing vocabulary. */
export type ConfidenceBand = 'high' | 'medium' | 'low';

/**
 * Band boundaries, in PERCENT. Inclusive lower bounds:
 *   >= 70 high · >= 40 medium · < 40 low
 * These are the values the Inspector and the tree were already using
 * independently; consolidating them changes no boundary, only who owns it.
 */
export const CONFIDENCE_HIGH_MIN = 70;
export const CONFIDENCE_MEDIUM_MIN = 40;

/** Kept as the legacy name so existing importers of the tree helper still read naturally. */
export const LOW_CONFIDENCE_THRESHOLD = CONFIDENCE_MEDIUM_MIN;

/** The subset of a document row this module needs. */
export interface ConfidenceSource {
  readonly ClassificationConfidence?: number | null;
  readonly ConfidenceScore?: number | null;
}

function normalise(raw: number): number {
  // ≤ 1 is a 0–1 float; above that it is already a percentage.
  return raw <= 1 ? Math.round(raw * 100) : Math.round(raw);
}

/**
 * The document's classifier confidence as a percentage 0–100, or `null` when the
 * row carries no usable score.
 *
 * Resolution order — authoritative column first:
 *  1. `ClassificationConfidence` when present and non-null.
 *  2. `ConfidenceScore` when present, non-null **and non-zero** (a stored 0 on
 *     the legacy column means "never populated"; see the module docstring).
 *  3. `null`.
 */
export function confidencePercent(doc: ConfidenceSource | null | undefined): number | null {
  if (!doc) return null;

  const primary = doc.ClassificationConfidence;
  if (primary !== null && primary !== undefined && Number.isFinite(primary)) {
    return normalise(primary);
  }

  const legacy = doc.ConfidenceScore;
  if (legacy !== null && legacy !== undefined && Number.isFinite(legacy) && legacy !== 0) {
    return normalise(legacy);
  }

  return null;
}

/** The band for an already-normalised percentage. */
export function bandForPercent(pct: number): ConfidenceBand {
  if (pct >= CONFIDENCE_HIGH_MIN) return 'high';
  if (pct >= CONFIDENCE_MEDIUM_MIN) return 'medium';
  return 'low';
}

/** The document's band, or `null` when it has no usable score. */
export function confidenceBandFor(doc: ConfidenceSource | null | undefined): ConfidenceBand | null {
  const pct = confidencePercent(doc);
  return pct === null ? null : bandForPercent(pct);
}

/**
 * Whether this document should carry the amber "low conf." treatment.
 *
 * A row with NO score is not low-confidence — it is unscored, and flagging it
 * amber would put the badge on every un-classified document. This is the
 * predicate the tree badge, the section roll-up and the document rows all use.
 */
export function isLowConfidence(doc: ConfidenceSource | null | undefined): boolean {
  return confidenceBandFor(doc) === 'low';
}
